import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Sparkles,
  X,
  AlertCircleIcon,
  ArrowRightIcon,
  RocketIcon,
  CircleDashedIcon,
} from "lucide-react";
import { BriefSection } from "@/types/types";
import { CreatorPortalToggle } from "./CreatorPortalToggle";
import {
  MessageVariables,
  ACTIVATION_MESSAGE_VARIABLES,
} from "./MessageVariables";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FrameFooter,
} from "@/components/ui/frame";

interface BriefSectionEditorProps {
  section: BriefSection;
  content: string;
  onChange: (content: string) => void;
  onSave: (content: string) => void;
  onClear: () => void;
  onAIGenerate: () => void;
  onNext: () => void;
  aiLoading: boolean;
  saving: boolean;
  isLastSection: boolean;
  isActivationMessage: boolean;
  hideNavigation: boolean;
  showAIButton: boolean;
  includeCreatorPortal?: boolean;
  onTogglePortal?: (value: boolean) => void;
  isPortalLoading?: boolean;
}

export function BriefSectionEditor({
  section,
  content,
  onChange,
  onSave,
  onClear,
  onAIGenerate,
  onNext,
  aiLoading,
  saving,
  isLastSection,
  isActivationMessage,
  hideNavigation,
  showAIButton,
  includeCreatorPortal = false,
  onTogglePortal,
  isPortalLoading = false,
}: BriefSectionEditorProps) {
  return (
    <div className="max-w-4xl w-full h-full flex flex-col gap-3 mx-auto">
      <Frame>
        <FrameHeader>
          <FrameTitle>{section.title}</FrameTitle>
          <FrameDescription>
            {section.required ? "Required" : "Optional"} • min. 20 characters
          </FrameDescription>
        </FrameHeader>
        <Textarea
          variant="panel"
          className="max-h-[40vh] min-h-[40vh] [scrollbar-color:--alpha(var(--foreground)/20%)_transparent] scrollbar-thin"
          value={content}
          onBlur={(e) => onSave(e.target.value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={section.placeholder}
        />
        {isActivationMessage && (
          <FrameFooter>
            <MessageVariables variables={ACTIVATION_MESSAGE_VARIABLES} message={content} />
          </FrameFooter>
        )}
        <FrameFooter className="flex items-center justify-end gap-2">
          <Button
            onClick={() => {
              onChange("---------------------");
              onSave("---------------------");
            }}
            disabled={aiLoading || !!content}
            variant="ghost"
            size="sm"
            className="mr-auto"
          >
            <CircleDashedIcon />
            Empty
          </Button>
          <Button
            onClick={onClear}
            disabled={aiLoading || !content}
            variant="destructive-ghost"
            size="sm"
          >
            <X />
            Clear
          </Button>
          {showAIButton && (
            <Button onClick={onAIGenerate} disabled={aiLoading} size="sm" className="group">
              {aiLoading ? <Spinner /> : <Sparkles className="group-hover:rotate-45 transition-all ease-out" />}
              Let Amy write
            </Button>
          )}
          {!hideNavigation && (
            <Button
              onClick={onNext}
              disabled={aiLoading || !content.trim() || saving}
              variant="neutral"
              className="group"
              size="sm"
            >
              {isLastSection ? "Complete" : "Next Section"}
              {!isLastSection && !saving && (
                <ArrowRightIcon className="group-hover:translate-x-0.5 transition-all" />
              )}
              {isLastSection && !saving && (
                <RocketIcon className="group-hover:translate-x-0.25 group-hover:-translate-y-0.25 transition-all" />
              )}
              {saving && <Spinner />}
            </Button>
          )}
        </FrameFooter>
      </Frame>

      {!isActivationMessage && (
        <Alert variant="info">
          <AlertTitle>
            <AlertCircleIcon />
            Tip for: {section.title}
          </AlertTitle>
          <AlertDescription className="text-sm pb-2">
            {section.description}
          </AlertDescription>
        </Alert>
      )}

      {isActivationMessage && onTogglePortal && (
        <CreatorPortalToggle
          includeCreatorPortal={includeCreatorPortal}
          onToggle={onTogglePortal}
          isLoading={isPortalLoading}
        />
      )}
    </div>
  );
}
